import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import { QUICK_EDIT_ALL_FIELDS, SessionType, type QuickEditFieldName } from '@shared';
import clientIntakeFormSchema from '@shared/utils/clientIntakeFormSchema.json';
import { selectSession } from '@client/model/selectors';
import { STATE_AUTOFILL_OPTIONS, STATE_ZIP_CODES } from '@client/util/stateZipCodes';

const schema = clientIntakeFormSchema as Record<string, { type: string; label: string; group: string }>;

const GENDER_OPTIONS = ['Male', 'Female', 'Intersex', "I don't know / prefer not to answer"];

const RACE_OPTIONS = [
  'White or European (e.g., German, Irish, English, Italian, Polish, French, etc.)',
  'Asian or Asian American (e.g., Chinese, Japanese, Filipino, Korean, South Asian, Vietnamese, etc.)',
  'Hispanic or Latino/a (e.g., Puerto Rican, Mexican, Cuban, Salvadoran, Colombian, etc.)',
  'Black or African American (e.g., Jamaican, Nigerian, Haitian, Ethiopian, etc.)',
  'Middle Eastern or North African (e.g., Lebanese, Iranian, Egyptian, Moroccan, Israeli, Palestinian, etc.)',
  'Native Hawaiian or Pacific Islander (e.g., Samoan, Guamanian, Chamorro, Tongan, etc.)',
  'American Indian or Alaska Native (e.g., Navajo Nation, Blackfeet Tribe, Inupiat Traditional Government, etc.)',
  "I don't know / prefer not to answer",
];

const DISABILITY_OPTIONS = ['Yes', 'No', "I don't know / prefer not to answer"];


const HEIGHT_OPTIONS: string[] = (() => {
  const options: string[] = [];
  for (let inches = 48; inches <= 84; inches++) {
    const feet = Math.floor(inches / 12);
    const rem = inches % 12;
    options.push(`${feet}'${rem}"`);
  }
  return options;
})();

// Height is stored as integer inches but displayed/selected as a feet'inches" string.
// These helpers keep the two representations in sync so we never persist the raw
// dropdown string (which the integer DB formatter would turn into "NaN").
function formatHeightDisplay(val: string | number | null | undefined): string {
  if (val == null) return '';
  const str = String(val);
  if (str.includes("'")) return str;
  const inches = Number(val);
  if (!isNaN(inches) && inches > 0) {
    return `${Math.floor(inches / 12)}'${inches % 12}"`;
  }
  return str;
}

function parseHeightToInches(val: string): number | null {
  const match = val.match(/(\d+)'[\s]*(\d+)/);
  if (!match) return null;
  return parseInt(match[1], 10) * 12 + parseInt(match[2], 10);
}

const SELECT_FIELDS: Record<string, string[]> = {
  clientGenderAtBirth: GENDER_OPTIONS,
  clientRace: RACE_OPTIONS,
  clientHeightInches: HEIGHT_OPTIONS,
  clientDisabilityWorkersComp: DISABILITY_OPTIONS,
};

const FULL_WIDTH_FIELDS = new Set<string>([
  'clientRace',
]);

const FIELD_GROUPS: { label: string; fields: QuickEditFieldName[] }[] = [
  {
    label: 'Contact',
    fields: ['clientFirstName', 'clientLastName', 'clientZipCode', 'clientLtcZipCode'],
  },
  {
    label: 'Demographic',
    fields: [
      'clientBirthDate',
      'clientGenderAtBirth',
      'clientRace',
      'householdHouseholdSize',
      'householdLivingChildrenCount',
      'clientLivingSiblingsCount',
    ],
  },
  {
    label: 'Health',
    fields: ['clientHeightInches', 'clientWeightPounds'],
  },
  {
    label: 'Financial',
    fields: ['clientDisabilityWorkersComp', 'householdTotalAssetsValue'],
  },
];

const ZIP_REGEX = /^\d{5}$/;

const OPTIONAL_FIELDS = new Set<string>([
  'clientFinancialSecurityPreference',
  'clientIndependencePreference',
  'clientFamilyInvolvementPreference',
  'clientHasDifferentLtcZipCode',
]);

const HOUSEHOLD_FIELDS = new Set<string>([
  'householdHouseholdSize',
  'householdLivingChildrenCount',
  'clientLivingSiblingsCount',
]);

export const QUICK_EDIT_FORM_ID = 'quick-edit-intake-form';

interface QuickEditFormProps {
  intakeSurvey: IntakeSurvey;
  onCancel: () => void;
  onSave: (fields: Record<string, string | number | null>) => void;
  onValidChange?: (valid: boolean) => void;
}


function isRequired(fieldName: string): boolean {
  return !OPTIONAL_FIELDS.has(fieldName);
}

function getFieldError(fieldName: string, value: string): string | null {
  if (isRequired(fieldName) && !value.trim()) {
    return 'This field is required.';
  }
  if (
    (fieldName === 'clientZipCode' || fieldName === 'clientLtcZipCode') &&
    value.length > 0 &&
    !ZIP_REGEX.test(value)
  ) {
    return 'Zip code must be exactly 5 digits.';
  }
  if (HOUSEHOLD_FIELDS.has(fieldName) && value) {
    const num = Number(value);
    if (isNaN(num) || num < 0 || num > 50) {
      return 'Value must be between 0 and 50.';
    }
  }
  return null;
}

function FieldLabel({ fieldName, label, htmlFor }: { fieldName: string; label: string; htmlFor: string }) {
  return (
    <Label htmlFor={htmlFor}>
      {label}
      {isRequired(fieldName) && <span className="ml-0.5 text-red-500">*</span>}
    </Label>
  );
}

function FieldError({ error }: { error: string | null }) {
  if (!error) return null;
  return <p className="text-xs text-red-500">{error}</p>;
}

const onlyDigitsKeyDown = (e: React.KeyboardEvent) => {
  if (
    !/[\d]/.test(e.key) &&
    !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key) &&
    !e.ctrlKey &&
    !e.metaKey
  ) {
    e.preventDefault();
  }
};

export function QuickEditForm({ intakeSurvey, onCancel, onSave, onValidChange }: QuickEditFormProps) {
  const { sessionType } = useSelector(selectSession);
  const isWizardSession = sessionType === SessionType.wizard;

  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const fieldName of QUICK_EDIT_ALL_FIELDS) {
      const val = intakeSurvey[fieldName as keyof IntakeSurvey];
      if (val == null) {
        initial[fieldName] = '';
      } else if (fieldName === 'clientHeightInches') {
        initial[fieldName] = formatHeightDisplay(val as string | number);
      } else if (schema[fieldName]?.type === 'date') {
        const d = new Date(val as string);
        initial[fieldName] = isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
      } else {
        initial[fieldName] = String(val);
      }
    }
    return initial;
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const markTouched = useCallback((fieldName: string) => {
    setTouched(prev => (prev[fieldName] ? prev : { ...prev, [fieldName]: true }));
  }, []);

  const [hasDifferentLtcZip, setHasDifferentLtcZip] = useState<boolean>(() => {
    const zip = intakeSurvey.clientZipCode;
    const ltcZip = intakeSurvey.clientLtcZipCode;
    return zip != null && ltcZip != null && String(zip) !== String(ltcZip);
  });

  const handleChange = (fieldName: string, value: string) => {
    setValues(prev => {
      const next = { ...prev, [fieldName]: value };
      if (fieldName === 'clientZipCode' && !hasDifferentLtcZip) {
        next.clientLtcZipCode = value;
      }
      return next;
    });
  };

  const handleLtcZipToggle = (checked: boolean) => {
    setHasDifferentLtcZip(checked);
    if (!checked) {
      setValues(prev => ({ ...prev, clientLtcZipCode: prev.clientZipCode }));
    }
  };

  const isFormValid = useMemo(() => {
    for (const fieldName of QUICK_EDIT_ALL_FIELDS) {
      if (getFieldError(fieldName, values[fieldName] ?? '')) return false;
    }
    return true;
  }, [values]);

  useEffect(() => {
    onValidChange?.(isFormValid);
  }, [isFormValid, onValidChange]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    const changedFields: Record<string, string | number | null> = {};
    for (const fieldName of QUICK_EDIT_ALL_FIELDS) {
      const original = intakeSurvey[fieldName as keyof IntakeSurvey];
      let originalStr = original != null ? String(original) : '';
      if (schema[fieldName]?.type === 'date' && original != null) {
        const d = new Date(original as string);
        originalStr = isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
      } else if (fieldName === 'clientHeightInches') {
        originalStr = formatHeightDisplay(original as string | number | null);
      }
      const newVal = values[fieldName];

      if (newVal !== originalStr) {
        const fieldSchema = schema[fieldName];
        if (!fieldSchema) continue;

        if (newVal === '') {
          changedFields[fieldName] = null;
        } else if (fieldName === 'clientHeightInches') {
          changedFields[fieldName] = parseHeightToInches(newVal);
        } else if (fieldSchema.type === 'integer') {
          changedFields[fieldName] = parseInt(newVal, 10);
        } else if (fieldSchema.type === 'number') {
          changedFields[fieldName] = parseFloat(newVal);
        } else {
          changedFields[fieldName] = newVal;
        }
      }
    }

    const originalHasDifferent =
      intakeSurvey.clientZipCode != null &&
      intakeSurvey.clientLtcZipCode != null &&
      String(intakeSurvey.clientZipCode) !== String(intakeSurvey.clientLtcZipCode);
    if (hasDifferentLtcZip !== originalHasDifferent) {
      changedFields.clientHasDifferentLtcZipCode = hasDifferentLtcZip ? 'Yes' : 'No';
    }

    if (Object.keys(changedFields).length === 0) {
      onCancel();
      return;
    }

    onSave(changedFields);
  };

  const renderField = (fieldName: QuickEditFieldName) => {
    if (fieldName === 'clientLtcZipCode') return null;

    const fieldSchema = schema[fieldName];
    if (!fieldSchema) return null;

    const id = `quick-edit-${fieldName}`;
    const value = values[fieldName] ?? '';
    const selectOptions = SELECT_FIELDS[fieldName];
    const isFullWidth = FULL_WIDTH_FIELDS.has(fieldName);
    const wrapperClass = isFullWidth ? 'space-y-1 sm:col-span-2' : 'space-y-1';
    const isTouched = touched[fieldName];
    const error = isTouched ? getFieldError(fieldName, value) : null;

    if (selectOptions) {
      return (
        <div
          key={fieldName}
          className={wrapperClass}
        >
          <FieldLabel fieldName={fieldName} label={fieldSchema.label} htmlFor={id} />
          <Select
            value={value}
            onValueChange={val => {
              handleChange(fieldName, val);
              markTouched(fieldName);
            }}
          >
            <SelectTrigger
              id={id}
              className="w-full min-w-0"
              onBlur={() => markTouched(fieldName)}
            >
              <SelectValue
                placeholder={`Select ${fieldSchema.label.toLowerCase()}`}
                className="truncate"
              />
            </SelectTrigger>
            <SelectContent>
              {selectOptions.map(opt => (
                <SelectItem
                  key={opt}
                  value={opt}
                >
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError error={error} />
        </div>
      );
    }

    let inputType = 'text';
    if (fieldSchema.type === 'integer' || fieldSchema.type === 'number') {
      inputType = 'number';
    } else if (fieldSchema.type === 'date') {
      inputType = 'date';
    }

    if (fieldName === 'clientZipCode') {
      const ltcZipValue = values.clientLtcZipCode ?? '';
      const ltcTouched = touched.clientLtcZipCode;
      const zipError = isTouched ? getFieldError('clientZipCode', value) : null;
      const ltcZipError = ltcTouched ? getFieldError('clientLtcZipCode', ltcZipValue) : null;
      const ltcFieldSchema = schema.clientLtcZipCode;

      const autofillState =
        STATE_AUTOFILL_OPTIONS.find(({ abbr }) => STATE_ZIP_CODES[abbr] === value)?.abbr ?? '';

      return (
        <div key={fieldName} className="space-y-4 sm:col-span-2">
          {isWizardSession && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="quick-edit-stateZipAutofill">Autofill zip code by state (admin only)</Label>
                <Select
                  value={autofillState}
                  onValueChange={abbr => {
                    handleChange('clientZipCode', STATE_ZIP_CODES[abbr]);
                    markTouched('clientZipCode');
                  }}
                >
                  <SelectTrigger id="quick-edit-stateZipAutofill" className="w-full min-w-0">
                    <SelectValue placeholder="Select a state" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATE_AUTOFILL_OPTIONS.map(({ abbr, name }) => (
                      <SelectItem
                        key={abbr}
                        value={abbr}
                      >
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <FieldLabel fieldName={fieldName} label={fieldSchema.label} htmlFor={id} />
              <Input
                id={id}
                type="text"
                value={value}
                onChange={e => handleChange(fieldName, e.target.value)}
                onBlur={() => markTouched(fieldName)}
                maxLength={5}
                minLength={5}
                inputMode="numeric"
                onKeyDown={onlyDigitsKeyDown}
              />
              <FieldError error={zipError} />
            </div>
            <div className="space-y-1">
              <FieldLabel fieldName="clientLtcZipCode" label={ltcFieldSchema?.label ?? 'Zip Code (during LTC)'} htmlFor="quick-edit-clientLtcZipCode" />
              <Input
                id="quick-edit-clientLtcZipCode"
                type="text"
                value={ltcZipValue}
                onChange={e => handleChange('clientLtcZipCode', e.target.value)}
                onBlur={() => markTouched('clientLtcZipCode')}
                disabled={!hasDifferentLtcZip}
                maxLength={5}
                minLength={5}
                inputMode="numeric"
                onKeyDown={onlyDigitsKeyDown}
              />
              <FieldError error={ltcZipError} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="quick-edit-hasDifferentLtcZip"
              checked={hasDifferentLtcZip}
              onCheckedChange={handleLtcZipToggle}
            />
            <Label htmlFor="quick-edit-hasDifferentLtcZip" className="text-sm font-normal">
              Use a different zip code during LTC
            </Label>
          </div>
        </div>
      );
    }

    return (
      <div
        key={fieldName}
        className={wrapperClass}
      >
        <FieldLabel fieldName={fieldName} label={fieldSchema.label} htmlFor={id} />
        <Input
          id={id}
          type={inputType}
          value={value}
          onChange={e => handleChange(fieldName, e.target.value)}
          onBlur={() => markTouched(fieldName)}
          {...(HOUSEHOLD_FIELDS.has(fieldName) ? { min: 0, max: 50 } : {})}
        />
        <FieldError error={error} />
      </div>
    );
  };

  return (
    <form
      id={QUICK_EDIT_FORM_ID}
      onSubmit={handleSubmit}
      className="mt-6 space-y-8"
      data-hj-suppress
      data-private
    >
      {FIELD_GROUPS.map(group => (
        <div key={group.label}>
          <h3 className="mb-4 text-sm font-semibold text-gray-700">{group.label}</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{group.fields.map(renderField)}</div>
        </div>
      ))}
    </form>
  );
}
